/**
 * @license
 * Copyright (c) 2014 The Polymer Project Authors. All rights reserved.
 * This code may only be used under the BSD style license found at http://polymer.github.io/LICENSE.txt
 * The complete set of authors may be found at http://polymer.github.io/AUTHORS.txt
 * The complete set of contributors may be found at http://polymer.github.io/CONTRIBUTORS.txt
 * Code distributed by Google as part of the polymer project is also
 * subject to an additional IP rights grant found at http://polymer.github.io/PATENTS.txt
 */
import ChildRunner from './childrunner.js';
import * as util   from './util.js';

var SOCKETIO_ENDPOINT = window.location.protocol + '//' + window.location.host;
var SOCKETIO_LIBRARY  = SOCKETIO_ENDPOINT + '/socket.io/socket.io.js';

/**
 * A socket for communication between the CLI and browser runners.
 *
 * @param {string} browserId An ID generated by the CLI runner.
 * @param {!io.Socket} socket The socket.io `Socket` to communicate over.
 */
export default function CLISocket(browserId, socket) {
  this.browserId = browserId;
  this.socket    = socket;
}

/**
 * @param {!Mocha.Runner} runner The Mocha `Runner` to observe, reporting
 *     interesting events back to the CLI runner.
 */
CLISocket.prototype.observe = function observe(runner) {
  this.writable = true;
  this.emitEvent('browser-start', {
    url: window.location.toString(),
  });

  // We only emit a subset of events that we care about, and follow a more
  // general event format that is hopefully applicable to test runners beyond
  // mocha.
  //
  // For all possible mocha events, see:
  // https://github.com/visionmedia/mocha/blob/master/lib/runner.js#L36
  runner.on('test', function(test) {
    this.emitEvent('test-start', {test: getTitles(test)});
  }.bind(this));

  runner.on('test end', function(test) {
    this.emitEvent('test-end', {
      state:    getState(test),
      test:     getTitles(test),
      duration: test.duration,
      error:    test.err,
    });
  }.bind(this));

  runner.on('fail', function(test, err) {
    // fail the test run if we catch errors outside of a test function
    if (test.type !== 'test') {
      this.emitEvent('browser-fail', 'Error thrown outside of test function: ' + err.stack);
    }
  }.bind(this));

  runner.on('childRunner start', function(childRunner) {
    this.emitEvent('sub-suite-start', childRunner.share);
  }.bind(this));

  runner.on('childRunner end', function(childRunner) {
    this.emitEvent('sub-suite-end', childRunner.share);
  }.bind(this));

  runner.on('end', function() {
    this.emitEvent('browser-end');
  }.bind(this));
};

/**
 * @param {string} event The name of the event to fire.
 * @param {*} data Additional data to pass with the event.
 */
CLISocket.prototype.emitEvent = function emitEvent(event, data) {
  var self = this;
  var isPolling = false;
  var dataJSON = data ? JSON.stringify(data) : '';
  var dataSize = dataJSON.length;
  var chunkSize = 65536;
  var chunks = [];
  var chunk;
  var eventId;
  var transport;
  if (this.socket.io &&
      this.socket.io.engine &&
      this.socket.io.engine.transport &&
      this.socket.io.engine.transport.query &&
      this.socket.io.engine.transport.query.transport === 'polling') {
    isPolling = true;
    this.eventQueue = this.eventQueue || [];
  }
  if (isPolling) {
    eventId = this.browserId + ',' + event + ',' + Date.now();
    if (dataSize > chunkSize) {
      transport = this.socket.io.engine.transport;
      while (dataJSON) {
        chunks.push(dataJSON.substr(0, chunkSize));
        dataJSON = dataJSON.substr(chunkSize);
      }
      function processEventQueue() {
        var eventItem;
        while (eventItem = self.eventQueue.shift()) {
          self.socket.emit(eventItem.event, eventItem.data);
          if (eventItem.event === 'client-event-fragment') {
            break;
          }
        }
        if (self.eventQueue.length === 0) {
          self.writable = true;
          transport.removeListener('drain', processEventQueue);
        }
      }
      while (chunk = chunks.shift()) {
        this.eventQueue.push({
          event:   'client-event-fragment',
          data: {
            browserId: self.browserId,
            eventId:   eventId,
            event:     event,
            chunk:     chunk,
            last:      chunks.length === 0
          }
        });
      }
      if (this.writable) {
        this.writable = false;
        transport.on('drain', processEventQueue);
        if (transport.writable) {
          processEventQueue();
        }
      }
    }
    else {
      if (this.writable) {
        this.socket.emit('client-event', {
          browserId: this.browserId,
          event:     event,
          data:      data,
        });
      }
      else {
        this.eventQueue.push({
          event:   'client-event',
          data: {
            browserId: this.browserId,
            event:     event,
            data:      data,
          }
        });
      }
    }
  }
  else {
    this.socket.emit('client-event', {
      browserId: this.browserId,
      event:     event,
      data:      data,
    });
  }
};

/**
 * Builds a `CLISocket` if we are within a CLI-run environment; short-circuits
 * otherwise.
 *
 * @param {function(*, CLISocket)} done Node-style callback.
 */
CLISocket.init = function init(done) {
  var browserId = util.getParam('cli_browser_id');
  if (!browserId) return done();
  // Only fire up the socket for root runners.
  if (ChildRunner.current()) return done();

  util.loadScript(SOCKETIO_LIBRARY, function(error) {
    if (error) return done(error);

    var socket = io(SOCKETIO_ENDPOINT);
    socket.on('error', function(error) {
      socket.off();
      done(error);
    });

    socket.on('connect', function() {
      socket.off();
      done(null, new CLISocket(browserId, socket));
    });
  });
};

// Misc Utility

/**
 * @param {!Mocha.Runnable} runnable The test or suite to extract titles from.
 * @return {!Array.<string>} The titles of the runnable and its parents.
 */
function getTitles(runnable) {
  var titles = [];
  while (runnable && !runnable.root && runnable.title) {
    titles.unshift(runnable.title);
    runnable = runnable.parent;
  }
  return titles;
}

/**
 * @param {!Mocha.Runnable} runnable
 * @return {string}
 */
function getState(runnable) {
  if (runnable.state === 'passed') {
    return 'passing';
  } else if (runnable.state == 'failed') {
    return 'failing';
  } else if (runnable.pending) {
    return 'pending';
  } else {
    return 'unknown';
  }
}
